import { collection, getDocs, query, where } from 'firebase/firestore';
import { useFirestore, useFunctions, useUserCompany } from '../hooks/useFirebase';
import { contractProgressType } from '../interfaces/contractProgressTypes';
import { DailyPostType } from '../interfaces/dailyPostType';
import { FormStateType } from '../interfaces/formTypes';
import { contractType } from '../pages/OwnContracts/components/List/List';

export const name = 'runQueryUrl';
export const description = 'Executes a statistical query from a generated query URL by calling the cloud query function and calculating costs, incomes, and results statistics. Returns calculated project statistics.';
export const parameters = {
  url: 'The statistics query URL generated by queryUrlCreator, e.g. "/statistics/query-graphs/?isDateRange=..."'
};
export const outputTextForUI = 'Lekérdezés futtatása';
export const useOnMobile = true;

export type incomeObjectType = { [key: string]: { contractProgresses: contractProgressType[], countedChange: number } };
type projectType = { project_number: string, other_costs: number, overhead_costs: number, overhead_percentage: number };
export type resultStatisticsObject = { costs: number, income: number, incomeMinusCosts: number, overheadPercentage: number, overheadCosts: number, otherCosts: number, result: number, resultPercentage: number };

export async function runQueryUrl(args: { url: string }) {
  if (!args || !args.url) {
    throw new Error('A "url" paraméter megadása kötelező.');
  }

  // 1. Parse query parameters from URL
  const parsedUrl = new URL(args.url, 'http://localhost');
  const searchParams = parsedUrl.searchParams;

  const isDateRangeString = searchParams.get('isDateRange');
  const dateRangeValueString = searchParams.get('dateRangeValue');
  const dateValueString = searchParams.get('dateValue');
  const companysListValueString = searchParams.get('companysListValue');
  const projectNumbersListValueString = searchParams.get('projectNumbersListValue');
  const datatablesListValueString = searchParams.get('datatablesListValue');

  const isDateRange = JSON.parse(isDateRangeString ? isDateRangeString : 'false');

  const parseDate = (dateStr: string | null) => {
    if (!dateStr || dateStr === 'null') return null;
    const normalized = dateStr.replace(/\.\s?/g, '-').replace(/-$/, '');
    const d = new Date(normalized);
    if (isNaN(d.getTime())) {
      return new Date(dateStr);
    }
    return d;
  };

  const dateRangeValue = dateRangeValueString
    ? dateRangeValueString.split(',').map((date) => parseDate(date))
    : [null, null];

  const dateValue = parseDate(dateValueString);

  const companysListValue = companysListValueString ? companysListValueString.split(',').filter(x => x !== "") : [];
  const projectNumbersListValue = projectNumbersListValueString ? projectNumbersListValueString.split(',').filter(x => x !== "") : [];
  const datatablesListValue = datatablesListValueString ? datatablesListValueString.split(',').filter(x => x !== "") : [];

  // 2. Call Firebase /query function
  const queryFn = useFunctions('query');
  const userCompany = await useUserCompany();

  const results: any = await queryFn({
    userCompany: userCompany,
    timezoneDifference: new Date().getTimezoneOffset() / 60 / -1,
    isDateRange: isDateRange,
    dateRangeValue: dateRangeValue,
    dateValue: dateValue,
    companysListValue: companysListValue,
    projectNumbersListValue: projectNumbersListValue,
    datatablesListValue: datatablesListValue
  });

  const rawCostDocuments = results.data.resultIWithAddedContractProgresses.dailys as DailyPostType[];
  const rawIncomeObject = results.data.resultWithOwnContractProgressses.object as incomeObjectType;

  // 3. Process cost documents
  const costDocuments = calc(rawCostDocuments, datatablesListValue, companysListValue, projectNumbersListValue);

  const costStatisticsByCompanys = makeCostsDataByCompanysToShowableFormat(costDocuments);
  const costStatisticsByDatatables = makeCostsDataByDatatablesToShowableFormat(costDocuments);
  const costStatisticsByProjectNumbers = makeCostsDataByProjectNumbersToShowableFormat(costDocuments);

  // 4. Process income object
  const incomeStatisticsByCompanys = makeIncomeDataToShowableFormat(rawIncomeObject, 'byContractors');
  const incomeStatisticsByProjectNumbers = makeIncomeDataToShowableFormat(rawIncomeObject, 'byProjectNumbers');

  // 5. Fetch projects and contracts details
  const { projectsList, ownContractsList } = await fetchProjectDetailsAndOwnContracts(costDocuments, rawIncomeObject);

  // 6. Calculate results
  const resultStatisticsByProjectNumbers = makeResultsToShowableFormat(projectsList, costDocuments, rawIncomeObject);
  const resultStatisticsByCustomers = makeResultsToShowableFormat(projectsList, costDocuments, rawIncomeObject, ownContractsList);

  const resultStatisticsInMonthly = combineStatistics(resultStatisticsByProjectNumbers.weekly, 'monthly');
  const resultStatisticsInYearly = combineStatistics(resultStatisticsByProjectNumbers.monthly, 'yearly');

  return {
    success: true,
    data: {
      costs: {
        byCompanies: costStatisticsByCompanys,
        byDatatables: costStatisticsByDatatables,
        byProjectNumbers: costStatisticsByProjectNumbers
      },
      incomes: {
        byCustomers: incomeStatisticsByCompanys,
        byProjectNumbers: incomeStatisticsByProjectNumbers
      },
      results: {
        byCustomers: resultStatisticsByCustomers,
        byProjectNumbers: resultStatisticsByProjectNumbers,
        monthly: resultStatisticsInMonthly,
        yearly: resultStatisticsInYearly
      }
    }
  };
}

// Re-implemented data processing methods from QueryGraphs.tsx
function calc(documents: DailyPostType[], datatablesListValue: string[], companysListValue: string[], projectNumbersListValue: string[]) {
  let filteredDocuments: DailyPostType[] = JSON.parse(JSON.stringify(documents));
  filteredDocuments = filteredDocuments.filter((doc) => doc.final_price !== 0);

  if (projectNumbersListValue.length > 0) {
    filteredDocuments = filteredDocuments.filter((doc) => projectNumbersListValue.includes(doc.project_number));
  }

  if (datatablesListValue.length > 0) {
    filteredDocuments.forEach((doc, docIndex) => {
      if (doc.data === "") return;
      const data = JSON.parse(doc.data) as FormStateType;
      const filteredData = data.filter((table) => datatablesListValue.includes(table.tableName));
      filteredDocuments[docIndex] = { ...doc, data: JSON.stringify(filteredData) };
    });
  }

  if (companysListValue.length > 0) {
    filteredDocuments.forEach((doc, docIndex) => {
      if (doc.data === "") return;
      const data = JSON.parse(doc.data) as FormStateType;
      const filteredData = data.map((table) => {
        let filteredTable = table.data.map((row) => {
          const rowHasNecesarryCompany = row.some((x) => companysListValue.includes(String(x.value)));
          return rowHasNecesarryCompany ? row : null;
        });
        const cleanedRows = filteredTable.filter((row) => row != null) as any[];
        return { tableName: table.tableName, data: cleanedRows };
      });
      filteredDocuments[docIndex] = { ...doc, data: JSON.stringify(filteredData) };
    });
  }

  filteredDocuments.forEach((doc, docIndex) => {
    if (doc.data === "") return;
    let dailySum = 0;
    let formData = undefined;
    try {
      formData = JSON.parse(doc.data) as FormStateType;
    } catch { }

    if (formData) {
      formData.forEach((table) => {
        table.data.forEach((row) => {
          const rowPrice = row.find((x) => x.column === 'counted_price');
          if (rowPrice) {
            dailySum += Number(rowPrice.value);
          }
        });
      });
    }

    let contractProgressSum = 0;
    doc.companies_inside_added_progresses?.forEach((company) => {
      contractProgressSum += company.price;
    });

    filteredDocuments[docIndex].final_price = dailySum + contractProgressSum;
  });

  return filteredDocuments;
}

function makeCostsDataByCompanysToShowableFormat(dailysWithAddedContractProgresses: DailyPostType[]) {
  const statisticsDaily: { [key: string]: { [key: string]: number } } = {};
  dailysWithAddedContractProgresses.forEach((daily) => {
    let dailydata = undefined;
    try {
      dailydata = JSON.parse(daily.data) as FormStateType;
    } catch { }

    const date = new Date(daily.date.seconds * 1000);
    const datestring = date.toLocaleDateString().replaceAll('. ', '-').replace('.', '');

    if (dailydata) {
      dailydata.forEach((table) => {
        const tableData = table.data;
        tableData.forEach((row) => {
          let companyIndex = row.find((obj) => obj.column === 'Cég');
          if (!companyIndex) {
            companyIndex = row.find((obj) => obj.column === 'Gyártó');
          }
          const company = String(companyIndex ? companyIndex.value : '');
          const priceIndex = row.find((obj) => obj.column === 'counted_price');
          const price = Number(priceIndex ? priceIndex.value : 0);
          if (price === 0) return;

          if (company !== '' && company !== undefined && company !== null && company !== 'null' && company !== 'undefined') {
            if (!statisticsDaily.hasOwnProperty(datestring)) {
              statisticsDaily[datestring] = {};
            }
            statisticsDaily[datestring][company] = (statisticsDaily[datestring][company] || 0) + price;
          }
        });
      });
    }

    daily.companies_inside_added_progresses?.forEach((progress) => {
      if (!statisticsDaily.hasOwnProperty(datestring)) {
        statisticsDaily[datestring] = {};
      }
      if (progress.price !== 0) {
        statisticsDaily[datestring][progress.contractor] = (statisticsDaily[datestring][progress.contractor] || 0) + progress.price;
      }
    });
  });

  const statisticsWeekly = convertToWeeklyReport(statisticsDaily);
  const statisticsMonthly = convertToMonthlyStatistics(statisticsDaily);
  const statisticsYearly = convertToYearlyReport(statisticsDaily);

  return {
    daily: Object.keys(statisticsDaily).length !== 0 ? statisticsDaily : undefined,
    weekly: Object.keys(statisticsWeekly).length !== 0 ? statisticsWeekly : undefined,
    monthly: Object.keys(statisticsMonthly).length !== 0 ? statisticsMonthly : undefined,
    yearly: Object.keys(statisticsYearly).length !== 0 ? statisticsYearly : undefined
  };
}

function makeCostsDataByDatatablesToShowableFormat(dailysWithAddedContractProgresses: DailyPostType[]) {
  const statisticsDaily: { [key: string]: { [key: string]: number } } = {};
  dailysWithAddedContractProgresses.forEach((daily) => {
    let dailydata = undefined;
    try {
      dailydata = JSON.parse(daily.data) as FormStateType;
    } catch { }

    const date = new Date(daily.date.seconds * 1000);
    const datestring = date.toLocaleDateString().replaceAll('. ', '-').replace('.', '');

    if (dailydata) {
      dailydata.forEach((table) => {
        const tableData = table.data;
        const tableName = table.tableName;
        const tableSumPrice = tableData.map((row) => {
          const index = row.findIndex((obj) => obj.column === 'counted_price');
          return index !== -1 ? Number(row[index].value) : 0;
        }).reduce((a, b) => a + b, 0);

        if (tableSumPrice === 0) return;

        if (!statisticsDaily.hasOwnProperty(datestring)) {
          statisticsDaily[datestring] = {};
        }
        statisticsDaily[datestring][tableName] = (statisticsDaily[datestring][tableName] || 0) + tableSumPrice;
      });
    }

    daily.companies_inside_added_progresses?.forEach((progress) => {
      if (!statisticsDaily.hasOwnProperty(datestring)) {
        statisticsDaily[datestring] = {};
      }
      if (progress.price !== 0) {
        statisticsDaily[datestring]['Alvállalkozói Szerződések'] = (statisticsDaily[datestring]['Alvállalkozói Szerződések'] || 0) + progress.price;
      }
    });
  });

  const statisticsWeekly = convertToWeeklyReport(statisticsDaily);
  const statisticsMonthly = convertToMonthlyStatistics(statisticsDaily);
  const statisticsYearly = convertToYearlyReport(statisticsDaily);

  return {
    daily: Object.keys(statisticsDaily).length !== 0 ? statisticsDaily : undefined,
    weekly: Object.keys(statisticsWeekly).length !== 0 ? statisticsWeekly : undefined,
    monthly: Object.keys(statisticsMonthly).length !== 0 ? statisticsMonthly : undefined,
    yearly: Object.keys(statisticsYearly).length !== 0 ? statisticsYearly : undefined
  };
}

function makeCostsDataByProjectNumbersToShowableFormat(dailysWithAddedContractProgresses: DailyPostType[]) {
  const statisticsDaily: { [key: string]: { [key: string]: number } } = {};
  dailysWithAddedContractProgresses.forEach((daily) => {
    const projectNumber = daily.project_number;
    const finalPrice = daily.final_price;
    const date = new Date(daily.date.seconds * 1000);
    const datestring = date.toLocaleDateString().replaceAll('. ', '-').replace('.', '');

    if (finalPrice === 0) return;

    if (!statisticsDaily.hasOwnProperty(datestring)) {
      statisticsDaily[datestring] = {};
    }

    if (projectNumber === '' || projectNumber === undefined || projectNumber === null) {
      statisticsDaily[datestring]['Ismeretlen munkaszám'] = (statisticsDaily[datestring]['Ismeretlen munkaszám'] || 0) + finalPrice;
    } else {
      statisticsDaily[datestring][projectNumber] = (statisticsDaily[datestring][projectNumber] || 0) + finalPrice;
    }
  });

  const statisticsWeekly = convertToWeeklyReport(statisticsDaily);
  const statisticsMonthly = convertToMonthlyStatistics(statisticsDaily);
  const statisticsYearly = convertToYearlyReport(statisticsDaily);

  return {
    daily: Object.keys(statisticsDaily).length !== 0 ? statisticsDaily : undefined,
    weekly: Object.keys(statisticsWeekly).length !== 0 ? statisticsWeekly : undefined,
    monthly: Object.keys(statisticsMonthly).length !== 0 ? statisticsMonthly : undefined,
    yearly: Object.keys(statisticsYearly).length !== 0 ? statisticsYearly : undefined
  };
}

function makeIncomeDataToShowableFormat(incomeObject: incomeObjectType, by: 'byContractors' | 'byProjectNumbers') {
  const statisticsDaily: { [key: string]: { [key: string]: number } } = {};
  Object.entries(incomeObject).forEach(([key, value]) => {
    const projectNumber = key.split('-----')[0];
    const dateString = key.split('-----')[1];
    const ownContractor = key.split('-----')[2];

    if (value.countedChange === 0) return;

    if (!statisticsDaily.hasOwnProperty(dateString)) {
      statisticsDaily[dateString] = {};
    }

    if (by === 'byContractors') {
      if (!statisticsDaily[dateString].hasOwnProperty(ownContractor)) {
        statisticsDaily[dateString][ownContractor] = 0;
      }
      statisticsDaily[dateString][ownContractor] += value.countedChange;
    }

    if (by === 'byProjectNumbers') {
      if (!statisticsDaily[dateString].hasOwnProperty(projectNumber)) {
        statisticsDaily[dateString][projectNumber] = 0;
      }
      statisticsDaily[dateString][projectNumber] += value.countedChange;
    }
  });

  const statisticsWeekly = convertToWeeklyReport(statisticsDaily);
  const statisticsMonthly = convertToMonthlyStatistics(statisticsDaily);
  const statisticsYearly = convertToYearlyReport(statisticsDaily);

  return {
    daily: Object.keys(statisticsDaily).length !== 0 ? statisticsDaily : undefined,
    weekly: Object.keys(statisticsWeekly).length !== 0 ? statisticsWeekly : undefined,
    monthly: Object.keys(statisticsMonthly).length !== 0 ? statisticsMonthly : undefined,
    yearly: Object.keys(statisticsYearly).length !== 0 ? statisticsYearly : undefined
  };
}

function makeResultsToShowableFormat(projectList: projectType[], dailysWithAddedContractProgresses: DailyPostType[], incomeObject: incomeObjectType, ownContractsList?: contractType[]) {
  let statisticsDaily: { [key: string]: { [key: string]: resultStatisticsObject } } = {};

  dailysWithAddedContractProgresses.forEach((daily) => {
    if (daily.project_number === '' || daily.project_number === null) return;
    const dailyDateString = `${new Date(daily.date.seconds * 1000).getFullYear()}-${new Date(daily.date.seconds * 1000).getMonth() + 1}-${new Date(daily.date.seconds * 1000).getDate()}`;

    if (!statisticsDaily.hasOwnProperty(dailyDateString)) {
      statisticsDaily[dailyDateString] = {};
    }
    if (!statisticsDaily[dailyDateString].hasOwnProperty(daily.project_number)) {
      statisticsDaily[dailyDateString][daily.project_number] = { costs: 0, income: 0, incomeMinusCosts: 0, overheadPercentage: 0, overheadCosts: 0, otherCosts: 0, result: 0, resultPercentage: 0 };
    }

    statisticsDaily[dailyDateString][daily.project_number] = {
      costs: statisticsDaily[dailyDateString][daily.project_number].costs + daily.final_price,
      income: 0,
      incomeMinusCosts: 0,
      overheadPercentage: 0,
      overheadCosts: 0,
      otherCosts: 0,
      result: 0,
      resultPercentage: 0
    };
  });

  Object.entries(incomeObject).forEach(([key, value]) => {
    const projectNumber = key.split('-----')[0];
    const dateString = key.split('-----')[1];

    if (!statisticsDaily.hasOwnProperty(dateString)) {
      statisticsDaily[dateString] = {};
    }
    if (!statisticsDaily[dateString].hasOwnProperty(projectNumber)) {
      statisticsDaily[dateString][projectNumber] = { costs: 0, income: 0, incomeMinusCosts: 0, overheadPercentage: 0, overheadCosts: 0, otherCosts: 0, result: 0, resultPercentage: 0 };
    }

    statisticsDaily[dateString][projectNumber] = {
      costs: (statisticsDaily[dateString][projectNumber].costs || 0),
      income: statisticsDaily[dateString][projectNumber].income + value.countedChange,
      incomeMinusCosts: 0,
      overheadPercentage: 0,
      overheadCosts: 0,
      otherCosts: 0,
      result: 0,
      resultPercentage: 0
    };
  });

  statisticsDaily = calculateOverheadAndOtherCosts(statisticsDaily, projectList);

  let statisticsWeekly = convertResultToWeeklyReport(statisticsDaily);
  statisticsWeekly = calculateOverheadAndOtherCosts(statisticsWeekly, projectList);

  let statisticsMonthly = convertResultToMonthlyStatistics(statisticsDaily);
  statisticsMonthly = calculateOverheadAndOtherCosts(statisticsMonthly, projectList);

  let statisticsYearly = convertResultToYearlyReport(statisticsDaily);
  statisticsYearly = calculateOverheadAndOtherCosts(statisticsYearly, projectList);

  if (ownContractsList) {
    statisticsDaily = convertByProjectNumberToByContractor(statisticsDaily, ownContractsList);
    statisticsWeekly = convertByProjectNumberToByContractor(statisticsWeekly, ownContractsList);
    statisticsMonthly = convertByProjectNumberToByContractor(statisticsMonthly, ownContractsList);
    statisticsYearly = convertByProjectNumberToByContractor(statisticsYearly, ownContractsList);
  }

  return {
    daily: Object.keys(statisticsDaily).length !== 0 ? statisticsDaily : undefined,
    weekly: Object.keys(statisticsWeekly).length !== 0 ? statisticsWeekly : undefined,
    monthly: Object.keys(statisticsMonthly).length !== 0 ? statisticsMonthly : undefined,
    yearly: Object.keys(statisticsYearly).length !== 0 ? statisticsYearly : undefined
  };
}

async function fetchProjectDetailsAndOwnContracts(costDocuments: DailyPostType[], incomeObject: incomeObjectType) {
  let projectNumbersToFilter = costDocuments.map((doc) => doc.project_number).filter((x) => x !== null && x !== "" && x !== undefined);
  Object.values(incomeObject).forEach((x) => {
    x.contractProgresses.forEach((y) => {
      projectNumbersToFilter.push(y.project_number);
    });
  });
  projectNumbersToFilter = [...new Set(projectNumbersToFilter)];

  const projectsList: projectType[] = [];
  const ownContractsList: contractType[] = [];

  if (projectNumbersToFilter.length !== 0) {
    const db = useFirestore();
    const userCompany = await useUserCompany();

    for (const projectNumber of projectNumbersToFilter) {
      let q = query(collection(db, "project"), where("company", "==", userCompany), where("project_number", "==", projectNumber));
      let querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        const docData = doc.data() as projectType;
        projectsList.push(docData);
      });

      q = query(collection(db, "contract"), where("contractor", "==", userCompany), where("project_number", "==", projectNumber));
      querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        const docData = doc.data() as contractType;
        ownContractsList.push(docData);
      });
    }
  }

  return { projectsList, ownContractsList };
}

function convertToWeeklyReport(dailyReport: Record<string, Record<string, number>>): Record<string, Record<string, number>> {
  const weeklyReport: Record<string, Record<string, number>> = {};
  for (const [date, companies] of Object.entries(dailyReport)) {
    const weekStartDate = getWeekStartDate(date);
    if (!weeklyReport.hasOwnProperty(weekStartDate)) {
      weeklyReport[weekStartDate] = {};
    }
    for (const [company, value] of Object.entries(companies)) {
      if (!weeklyReport[weekStartDate].hasOwnProperty(company)) {
        weeklyReport[weekStartDate][company] = 0;
      }
      weeklyReport[weekStartDate][company] += value;
    }
  }
  return weeklyReport;
}

function convertResultToWeeklyReport(dailyReport: Record<string, Record<string, resultStatisticsObject>>): Record<string, Record<string, resultStatisticsObject>> {
  const weeklyReport: Record<string, Record<string, resultStatisticsObject>> = {};
  for (const [date, companies] of Object.entries(dailyReport)) {
    const weekStartDate = getWeekStartDate(date);
    if (!weeklyReport.hasOwnProperty(weekStartDate)) {
      weeklyReport[weekStartDate] = {};
    }
    for (const [company, value] of Object.entries(companies)) {
      if (!weeklyReport[weekStartDate].hasOwnProperty(company)) {
        weeklyReport[weekStartDate][company] = { costs: 0, income: 0, incomeMinusCosts: 0, overheadPercentage: 0, overheadCosts: 0, otherCosts: 0, result: 0, resultPercentage: 0 };
      }
      weeklyReport[weekStartDate][company] = {
        ...weeklyReport[weekStartDate][company],
        costs: weeklyReport[weekStartDate][company].costs + value.costs,
        income: weeklyReport[weekStartDate][company].income + value.income
      };
    }
  }
  return weeklyReport;
}

function getWeekStartDate(dateString: string): string {
  const date = new Date(dateString);
  const dayOfWeek = date.getDay();
  const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  const weekStartDate = new Date(date.setDate(diff));
  return weekStartDate.toLocaleDateString().replaceAll('. ', '-').replace('.', '');
}

function convertToMonthlyStatistics(dailyReport: Record<string, Record<string, number>>): Record<string, Record<string, number>> {
  const monthlyStatistics: Record<string, Record<string, number>> = {};
  for (const [date, companies] of Object.entries(dailyReport)) {
    const parts = date.split('-');
    const year = parts[0];
    const month = parts[1];
    const monthKey = `${year}-${month}`;
    if (!monthlyStatistics.hasOwnProperty(monthKey)) {
      monthlyStatistics[monthKey] = {};
    }
    for (const [company, value] of Object.entries(companies)) {
      if (!monthlyStatistics[monthKey].hasOwnProperty(company)) {
        monthlyStatistics[monthKey][company] = 0;
      }
      monthlyStatistics[monthKey][company] += value;
    }
  }
  return monthlyStatistics;
}

function convertResultToMonthlyStatistics(dailyReport: Record<string, Record<string, resultStatisticsObject>>): Record<string, Record<string, resultStatisticsObject>> {
  const monthlyStatistics: Record<string, Record<string, resultStatisticsObject>> = {};
  for (const [date, companies] of Object.entries(dailyReport)) {
    const parts = date.split('-');
    const year = parts[0];
    const month = parts[1];
    const monthKey = `${year}-${month}`;
    if (!monthlyStatistics.hasOwnProperty(monthKey)) {
      monthlyStatistics[monthKey] = {};
    }
    for (const [company, value] of Object.entries(companies)) {
      if (!monthlyStatistics[monthKey].hasOwnProperty(company)) {
        monthlyStatistics[monthKey][company] = { costs: 0, income: 0, incomeMinusCosts: 0, overheadPercentage: 0, overheadCosts: 0, otherCosts: 0, result: 0, resultPercentage: 0 };
      }
      monthlyStatistics[monthKey][company] = {
        ...monthlyStatistics[monthKey][company],
        costs: monthlyStatistics[monthKey][company].costs + value.costs,
        income: monthlyStatistics[monthKey][company].income + value.income
      };
    }
  }
  return monthlyStatistics;
}

function convertToYearlyReport(dailyReport: Record<string, Record<string, number>>): Record<string, Record<string, number>> {
  const yearlyReport: Record<string, Record<string, number>> = {};
  for (const [date, companies] of Object.entries(dailyReport)) {
    const year = date.split('-')[0];
    if (!yearlyReport.hasOwnProperty(year)) {
      yearlyReport[year] = {};
    }
    for (const [company, value] of Object.entries(companies)) {
      if (!yearlyReport[year].hasOwnProperty(company)) {
        yearlyReport[year][company] = 0;
      }
      yearlyReport[year][company] += value;
    }
  }
  return yearlyReport;
}

function convertResultToYearlyReport(dailyReport: Record<string, Record<string, resultStatisticsObject>>): Record<string, Record<string, resultStatisticsObject>> {
  const yearlyReport: Record<string, Record<string, resultStatisticsObject>> = {};
  for (const [date, companies] of Object.entries(dailyReport)) {
    const year = date.split('-')[0];
    if (!yearlyReport.hasOwnProperty(year)) {
      yearlyReport[year] = {};
    }
    for (const [company, value] of Object.entries(companies)) {
      if (!yearlyReport[year].hasOwnProperty(company)) {
        yearlyReport[year][company] = { costs: 0, income: 0, incomeMinusCosts: 0, overheadPercentage: 0, overheadCosts: 0, otherCosts: 0, result: 0, resultPercentage: 0 };
      }
      yearlyReport[year][company] = {
        ...yearlyReport[year][company],
        costs: yearlyReport[year][company].costs + value.costs,
        income: yearlyReport[year][company].income + value.income
      };
    }
  }
  return yearlyReport;
}

function calculateOverheadAndOtherCosts(statistics: Record<string, Record<string, resultStatisticsObject>>, projectList: projectType[]): Record<string, Record<string, resultStatisticsObject>> {
  Object.entries(statistics).forEach(([date, projectObject]) => {
    Object.entries(projectObject).forEach(([projectNumber, object]) => {
      const income = object.income;
      const costs = object.costs;
      statistics[date][projectNumber].incomeMinusCosts = income - costs;
    });
  });

  const howManyTimesProjectNumbersExists: { [key: string]: number } = {};
  Object.entries(statistics).forEach(([_, projectObject]) => {
    Object.keys(projectObject).forEach((projectNumber) => {
      howManyTimesProjectNumbersExists[projectNumber] = (howManyTimesProjectNumbersExists[projectNumber] || 0) + 1;
    });
  });

  Object.entries(statistics).forEach(([date, projectObject]) => {
    Object.entries(projectObject).forEach(([projectNumber, object]) => {
      const incomeMinusCosts = object.incomeMinusCosts;
      const projectData = projectList.find((project) => project.project_number === projectNumber);
      const overheadPercentage = projectData?.overhead_percentage || 0;

      let overheadCosts = projectData?.overhead_costs || 0;
      overheadCosts = overheadCosts / howManyTimesProjectNumbersExists[projectNumber];
      overheadCosts = Math.round(overheadCosts);

      let otherCosts = projectData?.other_costs || 0;
      otherCosts = otherCosts / howManyTimesProjectNumbersExists[projectNumber];
      otherCosts = Math.round(otherCosts);

      statistics[date][projectNumber].overheadPercentage = overheadPercentage;
      statistics[date][projectNumber].overheadCosts = overheadCosts;
      statistics[date][projectNumber].otherCosts = otherCosts;

      const result = incomeMinusCosts - overheadCosts - otherCosts;
      statistics[date][projectNumber].result = result;

      const income = statistics[date][projectNumber].income;
      statistics[date][projectNumber].resultPercentage = income === 0 ? -100 : Number((result / (income || 1) * 100).toFixed(2));
    });
  });

  return statistics;
}

function convertByProjectNumberToByContractor(statistics: { [key: string]: { [key: string]: resultStatisticsObject } }, ownContractsList: contractType[]) {
  const newStatisticsObject: { [key: string]: { [key: string]: resultStatisticsObject } } = {};

  Object.entries(statistics).forEach(([date, projectObject]) => {
    Object.entries(projectObject).forEach(([projectNumber, object]) => {
      const contractor = ownContractsList.find((contract) => contract.project_number === projectNumber);
      const customer = contractor?.customer;

      if (contractor && customer) {
        if (!newStatisticsObject[date]) {
          newStatisticsObject[date] = {};
        }
        if (!newStatisticsObject[date][customer]) {
          newStatisticsObject[date][customer] = {
            costs: object.costs,
            income: object.income,
            incomeMinusCosts: object.incomeMinusCosts,
            overheadPercentage: object.overheadPercentage,
            overheadCosts: object.overheadCosts,
            otherCosts: object.otherCosts,
            result: object.result,
            resultPercentage: object.income === 0 ? -100 : object.resultPercentage
          };
        } else {
          const item = newStatisticsObject[date][customer];
          newStatisticsObject[date][customer] = {
            costs: item.costs + object.costs,
            income: item.income + object.income,
            incomeMinusCosts: item.incomeMinusCosts + object.incomeMinusCosts,
            overheadPercentage: Math.abs(Number(((item.overheadCosts + object.overheadCosts) / ((item.incomeMinusCosts + object.incomeMinusCosts) || 1) * 100).toFixed(2))),
            overheadCosts: item.overheadCosts + object.overheadCosts,
            otherCosts: item.otherCosts + object.otherCosts,
            result: item.result + object.result,
            resultPercentage: (item.income + object.income) === 0 ? -100 : Number(((item.result + object.result) / ((item.income + object.income) || 1) * 100).toFixed(2))
          };
        }
      }
    });
  });

  return newStatisticsObject;
}

function combineStatistics(statistics: { [key: string]: { [key: string]: resultStatisticsObject } } | undefined, mode: 'monthly' | 'yearly') {
  const newObject: { [key: string]: { [key: string]: resultStatisticsObject } } = {};

  if (!statistics) {
    return undefined;
  }

  Object.entries(statistics).forEach(([key, value]) => {
    const costs = Object.values(value).reduce((a, b) => a + b.costs, 0);
    const income = Object.values(value).reduce((a, b) => a + b.income, 0);
    const incomeMinusCosts = Object.values(value).reduce((a, b) => a + b.incomeMinusCosts, 0);
    const otherCosts = Object.values(value).reduce((a, b) => a + b.otherCosts, 0);
    const overheadPercentage = Object.values(value).reduce((a, b) => a + b.overheadPercentage, 0);
    const overheadCosts = Object.values(value).reduce((a, b) => a + b.overheadCosts, 0);
    const result = Object.values(value).reduce((a, b) => a + b.result, 0);
    const resultPercentage = Object.values(value).reduce((a, b) => a + b.resultPercentage, 0);

    if (mode === 'monthly') {
      const parts = key.split('-');
      const month = `${parts[0]}-${parts[1]}`;

      if (!newObject[month]) {
        newObject[month] = {};
      }
      newObject[month][key] = {
        costs: costs,
        income: income,
        incomeMinusCosts: incomeMinusCosts,
        overheadPercentage: overheadCosts / incomeMinusCosts * 100,
        overheadCosts: overheadCosts,
        otherCosts: otherCosts,
        result: result,
        resultPercentage: income === 0 ? -100 : Number((result / (income || 1) * 100).toFixed(2))
      };
    }
    if (mode === 'yearly') {
      const year = key.split('-')[0];

      if (!newObject[year]) {
        newObject[year] = {};
      }
      newObject[year][key] = {
        costs: costs,
        income: income,
        incomeMinusCosts: incomeMinusCosts,
        overheadPercentage: overheadCosts / incomeMinusCosts * 100,
        overheadCosts: overheadCosts,
        otherCosts: otherCosts,
        result: result,
        resultPercentage: income === 0 ? -100 : Number((result / (income || 1) * 100).toFixed(2))
      };
    }
  });

  return newObject;
}
